import './download-records.scss';

import Component from '@glimmer/component';
import {
  HdsModal,
  HdsButton,
  HdsButtonSet,
  HdsCardContainer,
  HdsTextDisplay,
  HdsFormRadioGroup,
  HdsAlert,
} from '@hashicorp/design-system-components/components';

import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { get } from '@ember/object';
import { service } from '@ember/service';

import type ReportingAnalyticsService from '../../../services/reporting-analytics';

export interface DownloadRecordsModalSignature {
  Args: {
    onClose?: () => void;
    onDownload: () => Promise<Record<string, unknown>[]>;
    csvFields: { key?: string; label: string }[];
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}

export default class DownloadRecordsModal extends Component<DownloadRecordsModalSignature> {
  @service declare readonly reportingAnalytics: ReportingAnalyticsService;

  @tracked
  format = 'json';
  @tracked
  isLoading = false;
  @tracked
  hasError = false;
  @tracked
  downloadUrl!: string;
  @tracked
  hasData = false;

  get isContinueShown() {
    return !this.isLoading && !this.hasData;
  }

  get downloadName() {
    return `download.${this.format}`;
  }

  get isCSV() {
    return this.format === 'csv';
  }

  handleFormatChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.format = target?.value;
  };

  startDownload = async () => {
    try {
      this.isLoading = true;
      const listData = await this.args.onDownload();
      this.hasData = true;
      if (this.format === 'json') {
        this.downloadUrl = this.createJSONDownloadUrl(listData);
      } else if (this.format === 'csv') {
        this.downloadUrl = this.createCSVDownloadUrl(listData);
      }
    } catch {
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  };

  onFileDownloaded = () => {
    this.reportingAnalytics.trackEvent('download_records', {
      downloadFormat: this.format,
    });
  };

  createJSONDownloadUrl = (listData: Record<string, unknown>[]) => {
    return `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(listData),
    )}`;
  };

  /**
   * Converts list of data to a CSV
   * - Each cell is quoted to allow for newlines and commas in the dta
   * - Quotes within are escaped with a double quote
   * https://www.rfc-editor.org/rfc/rfc4180
   * @param {ListData} listData
   */

  createCSVDownloadUrl = (listData: Record<string, unknown>[]) => {
    const headers = this.args.csvFields.map(({ label }) => label).join(',');
    const rows = listData
      .map((datum) =>
        this.args.csvFields
          .filter(({ key }) => Boolean(key))
          .map(({ key }) => {
            const cell = (get(datum, key || '') as string)
              .toString()
              .replace(/"/g, '""');
            return `"${cell}"`;
          })
          .join(','),
      )
      .join('\r\n');
    const csv = `${headers}\r\n${rows}`;
    return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  };
  <template>
    <HdsModal
      @onClose={{@onClose}}
      data-test-download-records-modal
      id="download-records"
      as |M|
    >
      <M.Header>
        Download records
      </M.Header>
      <M.Body>
        {{#if this.hasData}}
          <HdsCardContainer
            {{! @glint-expect-error }}
            @background="neutral-primary"
            @hasBorder={{true}}
            {{! @glint-expect-error }}
            @level="mid"
            class="download-records__download-card"
          >
            <HdsTextDisplay @size="200">
              Download records
            </HdsTextDisplay>
            <HdsButton
              @color="secondary"
              @href={{this.downloadUrl}}
              @icon="download"
              @isIconOnly={{true}}
              @text="Download"
              data-test-download-records-modal-download-button
              download={{this.downloadName}}
              {{on "click" this.onFileDownloaded}}
            />
          </HdsCardContainer>
          <HdsAlert
            class="download-records__disclaimer"
            @type="compact"
            @color="warning"
            as |A|
          >
            <A.Description>
              Please download the file before closing the popup.
            </A.Description>
          </HdsAlert>

        {{else if this.isLoading}}
          <div data-test-download-records-modal-loader>

            <HdsTextDisplay @size="200">
              {{#if this.isCSV}}
                Generating CSV file...
              {{else}}
                Generating JSON file...
              {{/if}}
            </HdsTextDisplay>
            <HdsAlert
              class="download-records__disclaimer"
              @type="compact"
              @color="warning"
              as |A|
            >
              <A.Description>
                Do not close this popup or refresh your browser.
              </A.Description>
            </HdsAlert>

          </div>
        {{else}}
          <HdsFormRadioGroup @name="format" as |G|>
            <G.Legend class="hds-typography-body-100-font-size">
              Select the download file format and click "Continue" to proceed.
            </G.Legend>
            <G.RadioField
              @value="json"
              data-test-download-records-modal-radio-json
              checked
              {{on "change" this.handleFormatChange}}
              as |F|
            >
              <F.Label>
                JSON
              </F.Label>
            </G.RadioField>
            <G.RadioField
              @value="csv"
              data-test-download-records-modal-radio-csv
              {{on "change" this.handleFormatChange}}
              as |F|
            >
              <F.Label>
                CSV
              </F.Label>
            </G.RadioField>
          </HdsFormRadioGroup>
          <HdsAlert
            class="download-records__disclaimer"
            @type="compact"
            @color="warning"
            as |A|
          >
            <A.Description>
              Downloads are limited to 1000 records at this time.
            </A.Description>
          </HdsAlert>
        {{/if}}
      </M.Body>
      <M.Footer as |F|>
        <HdsButtonSet>
          {{#if this.isContinueShown}}
            <HdsButton
              type="button"
              data-test-download-records-modal-submit
              @text="Continue"
              {{on "click" this.startDownload}}
            />
          {{/if}}
          <HdsButton
            type="button"
            data-test-download-records-modal-cancel
            @color="secondary"
            @text="Cancel"
            {{on "click" F.close}}
          />
        </HdsButtonSet>
      </M.Footer>
    </HdsModal>
  </template>
}
